import * as React from 'react';
import { WithChildrenCommonProps } from '@types';
import { useKeyPress, useToggle, PopoutPlacement } from '@hooks';
import { Input, InputProps } from '@components/form/input';
import { Highlight, defaultHighlightFn } from '@components/highlight';
import {
  DropdownOptionList,
  IDropdownOption,
} from '@components/dropdown-option-list';
import { Button } from '@components/button';
import { Icon, IconNames } from '@components/icon';
import { Option } from '@components/option';
import { getFieldChevronStylesAsClassName } from '../field/field-styles';
import { concatenateAndReplace } from '@utils/test-helpers';

export type ISelectAutocompleteOption = IDropdownOption<unknown>;

export type ISelectAutocomplete = Omit<
  InputProps,
  'name' | 'onChange' | 'value'
> &
  WithChildrenCommonProps<{
    /** Defines the width of the Dropdown element */
    dropdownWidth?: number | string;
    /** Indicates that any value can be used in the input, no need to be one of the options */
    freeInput?: boolean;
    /** Indicates the property to be used as value for the key Autocomplete options */
    /** Warning: If keyId is not defined, index is used and this could cause performance issues */
    keyId?: string;
    /**
     * Indicates if the SelectAutocomplete is opened
     * Note: this prop is used in case you want to open the autocomplete from the application. It is not needed to manage it from the application.
     * It cannot be used to close the autocomplete.
     * */
    isOpen?: boolean;
    /** Indicates the number of items to be displayed in the dropdown when it is opened */
    maxVisibleOptions?: number;
    /** Define the name of the input element */
    name: string;
    /** Defines the label to be displayed when there are no results */
    noResultsLabel: string;
    /** List of options to be displayed in the Dropdown */
    options: ISelectAutocompleteOption[];
    /** Text to display in the header of the option list */
    optionsHeaderText?: string;
    /** Flag to indicate whether the chevron icon is displayed */
    showRightIcon?: boolean;
    /** Indicates the position of the dropdown relative to the Input */
    placement?: PopoutPlacement;
    /** Indicates the value of the Select */
    value?: ISelectAutocompleteOption | null;
    /** Function to be executed when clicking outside the textfield */
    onBlur?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    /** Function to be executed when changing the selected value */
    onChange?: (name: string, value: ISelectAutocompleteOption | null) => void;
    /** Function to be executed when clicking outside the dropdown */
    onClickAway?: (value?: string) => void;
    /** Function to execute on field focus */
    onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
    /** Function to be executed when changing the input */
    onInputChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    /** Defines if label is left aligned */
    labelLeftAligned?: boolean;
  }>;

export const SearchSelectAutocomplete = ({
  dataId,
  disabled,
  dropdownWidth,
  freeInput = false,
  leftContent,
  isOpen,
  keyId,
  label,
  maxVisibleOptions = 5,
  name,
  noResultsLabel,
  optionsHeaderText,
  options,
  placeholder,
  placement = 'bottom-start',
  readOnly,
  showRightIcon = true,
  value,
  labelLeftAligned = false,
  onChange,
  onClear,
  onClickAway,
  onFocus,
  onInputChange,
  ...rest
}: ISelectAutocomplete): JSX.Element => {
  const [query, setQuery] = React.useState<string | undefined>(undefined);
  const [selectedInputValue, setSelectedInputValue] = React.useState<string>(
    value?.label || ''
  );
  const [focusedIndex, setFocusedIndex] = React.useState(0);
  const [editionMode, setEditionMode] = React.useState<boolean>(false);

  const { on: isDropdownOpen, toggle } = useToggle(false);
  const inputRef = React.useRef<HTMLDivElement>(null);

  const externalValue = React.useMemo(() => value?.label || '', [value?.label]);

  const filteredOptions = options.filter(
    (option: ISelectAutocompleteOption) => {
      if (!query) return options;

      return defaultHighlightFn(option.label, query).length > 1;
    }
  );

  const showDropdown = React.useMemo(
    () =>
      !readOnly &&
      !disabled &&
      (!freeInput || (freeInput && filteredOptions.length > 0)) &&
      isDropdownOpen,
    [readOnly, disabled, filteredOptions.length, freeInput, isDropdownOpen]
  );

  const handleOnClick = React.useCallback(() => {
    if (disabled || readOnly || !query) {
      return;
    }
    !isDropdownOpen && toggle();
  }, [query, disabled, readOnly, isDropdownOpen, toggle]);

  const rightIcon = React.useMemo(() => {
    if (!showRightIcon) return;

    const iconName = isDropdownOpen
      ? IconNames.CHEVRON_UP
      : IconNames.CHEVRON_DOWN;
    return (
      <Button
        variant={'text'}
        onClick={handleOnClick}
        className={getFieldChevronStylesAsClassName}
      >
        <Icon name={iconName} />
      </Button>
    );
  }, [isDropdownOpen, showRightIcon, handleOnClick]);

  React.useEffect(() => {
    if (isOpen) {
      document.getElementById(name)?.focus();
    }
  }, [isOpen, name]);

  const onFocusInput = (event: React.FocusEvent<HTMLInputElement>) => {
    handleOnClick();
    onFocus?.(event);
  };

  const handleClickAway = (selectedInputValue?: string) => {
    const value = selectedInputValue?.toLocaleLowerCase();

    const optionSelected = options.find(
      (option: ISelectAutocompleteOption) =>
        option.value.toLocaleLowerCase() === value ||
        option.label.toLocaleLowerCase() === value
    );

    if (!freeInput && optionSelected === undefined) {
      setSelectedInputValue('');
    }

    onClickAway?.(selectedInputValue);
    setQuery(undefined);
    setFocusedIndex(0);
    setEditionMode(false);
    isDropdownOpen && toggle();
  };

  const handleOnChange = (value: ISelectAutocompleteOption | null) => {
    // When the component is inside a form, on Enter key triggers the onchange of the Select.
    // if no option is selected, value is null so when this component is in freeInput we set the value of the input
    const selectedValue =
      freeInput && !value ? createOption(selectedInputValue) : value;

    setQuery('');
    onChange?.(name, selectedValue);
    setFocusedIndex(0);
    setEditionMode(false);
    toggle();
  };

  const handleOnInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    if (!editionMode) {
      setEditionMode(true);
      !freeInput && onChange?.(name, null);
      setSelectedInputValue('');
    }

    onInputChange?.(event);
    setSelectedInputValue(value);
    setQuery(value);
    setFocusedIndex(0);
    freeInput && onChange?.(name, createOption(value));
    !isDropdownOpen && toggle();
  };

  const handleOnClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    onChange?.(name, null);
    onClear?.(event);
  };

  const handleOnEnter = (event: React.KeyboardEvent) => {
    handleOnChange(filteredOptions[focusedIndex]);
    setEditionMode(false);
    event.preventDefault();
  };

  const handleMouseEnter = (selectedOption: Record<string, unknown>) => {
    const selectedOptionIndex = filteredOptions.findIndex(
      (option: ISelectAutocompleteOption) =>
        option.value === selectedOption.value
    );
    setFocusedIndex(selectedOptionIndex);
  };

  const handleArrowDown = () => {
    setFocusedIndex((current: number) =>
      filteredOptions[current + 1] ? current + 1 : current
    );
  };

  const handleArrowUp = () => {
    setFocusedIndex((current: number) =>
      filteredOptions[current - 1] ? current - 1 : current
    );
  };

  const handleOnTab = (event: React.KeyboardEvent) => {
    isDropdownOpen && event.preventDefault();
  };

  const handleOnEscape = () => {
    isDropdownOpen && toggle();
    setEditionMode(false);
  };

  const { handleKeyPress } = useKeyPress({
    onArrowDown: handleArrowDown,
    onArrowUp: handleArrowUp,
    onTab: handleOnTab,
    onEnter: handleOnEnter,
    onEscape: handleOnEscape,
  });

  return (
    <>
      <Input
        autoComplete="off"
        leftContent={leftContent}
        disabled={disabled}
        label={label}
        labelLeftAligned={labelLeftAligned}
        name={name}
        placeholder={placeholder}
        readOnly={readOnly}
        ref={inputRef}
        rightContent={rightIcon}
        value={editionMode ? selectedInputValue : externalValue}
        onChange={handleOnInputChange}
        onClear={onClear && handleOnClear}
        onClick={handleOnClick}
        onFocus={onFocusInput}
        onKeyDown={handleKeyPress}
        data-testId={concatenateAndReplace(name)}
        {...rest}
      />
      {query && (
        <DropdownOptionList
          data-id={dataId}
          dropdownWidth={dropdownWidth}
          isOpen={showDropdown ? showDropdown : false}
          optionsHeaderText={optionsHeaderText}
          placement={placement}
          reference={inputRef.current as HTMLElement}
          maxVisibleOptions={maxVisibleOptions}
          onChange={handleOnChange}
          onClickAway={handleClickAway}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map(
              (item: ISelectAutocompleteOption, index: number) => (
                <Option
                  focused={filteredOptions[focusedIndex]?.value === item.value}
                  // @ts-ignore
                  key={keyId ? item[keyId] : index}
                  value={item}
                  selected={value?.value === item.value}
                  // @ts-ignore
                  onMouseEnter={handleMouseEnter}
                  data-testid={`select-autocomplete-option-${
                    // @ts-ignore
                    keyId ? item[keyId] : index
                  }`}
                >
                  <div>
                    {item?.leftContent}
                    <Highlight
                      query={query}
                      className={item?.leftContent ? 'ml-2' : ''}
                    >
                      {item.label}
                    </Highlight>
                  </div>
                </Option>
              )
            )
          ) : (
            <Option value="">{noResultsLabel}</Option>
          )}
        </DropdownOptionList>
      )}
    </>
  );
};

function createOption(value: string): ISelectAutocompleteOption {
  return { label: value, value };
}
